/**
 * CPB Bridge
 * ----------
 * Subscribes to Custom Product Builder's native events and exposes a clean,
 * structured change stream for the viewer.
 *
 * Signals observed (discovered in assets/customproductbuilder.js — CPB emits
 * these on document.body):
 *   - `CPB_ON_PRODUCT_MOUNTED`  — fired once after CPB has rendered the panel
 *   - `CPB_ON_OPTIONS_UPDATE`   — fired on every user-driven option change
 *
 * State lives at `window.CPB.getStore().getState().Product`:
 *   - panels[]                        — top-level configurator sections ("STEP 1 …")
 *   - panels[].categories[].options[] — full option catalogue (id, label, img…)
 *   - layers[]                        — currently-selected option per category
 *                                       ({ category, option, panel, value, … })
 *
 * We publish two streams via the public API:
 *   - `onCpbReady(cb)`   — fires once with the initial Product state + layers
 *   - `onCpbChange(cb)`  — fires on every options update with { layers, changed }
 *
 * The `changed` payload is a category-level diff with opaque CPB UUIDs resolved
 * to human-readable titles/labels by walking the `panels` catalogue. Consumers
 * can branch on `category.title === 'Shape'` / `next.label === 'Square'` without
 * having to write their own lookup.
 *
 * Debug handle (dev only): `window.__cpbBridge.{ getProduct, getLayers }`.
 */

type CpbLayer = {
  id?: string | number;
  category?: string;
  option?: string;
  panel?: string;
  /** Free-form value attached to a layer (typed text, uploaded image URL, …). */
  value?: string;
  preview?: unknown;
  price?: number | string;
  [k: string]: unknown;
};

/**
 * Real CPB runtime shape (confirmed by live inspection on 2026-04-22): each
 * entry in `category.options[]` is a wrapper object whose `.option.data` holds
 * the label + value. Our earlier assumption that `data` sat on the wrapper
 * itself matched the type definitions but not the actual payload — which made
 * every resolved label/value come back `undefined` and silently broke the
 * mapper. The nested shape is what we read from now; the outer `data` is kept
 * as an optional fallback in case CPB ever flattens it back.
 */
type CpbOption = {
  id?: string;
  option?: {
    data?: {
      label?: string;
      value?: string;
      [k: string]: unknown;
    };
    [k: string]: unknown;
  };
  data?: {
    label?: string;
    value?: string;
    [k: string]: unknown;
  };
  metafields?: unknown[];
  [k: string]: unknown;
};

type CpbCategory = {
  id?: string;
  title?: string;
  display?: string;
  options?: CpbOption[];
  metafields?: unknown[];
  [k: string]: unknown;
};

type CpbPanel = {
  id?: string;
  title?: string;
  categories?: CpbCategory[];
  [k: string]: unknown;
};

type CpbProductState = {
  panels?: CpbPanel[];
  layers?: CpbLayer[];
  [k: string]: unknown;
};

/** Category identity resolved from an opaque UUID to its human-readable context. */
export type ResolvedCategory = {
  id: string;
  title?: string;
  panelId?: string;
  panelTitle?: string;
};

/** Option identity resolved against the option catalogue. */
export type ResolvedOption = {
  id: string;
  label?: string;
  /** Static value from the catalogue (image URL, color hex, etc.). */
  value?: string;
  /**
   * The layer's free-form value at the time of change — for open-ended inputs
   * (text fields, uploaded images) this is the user-entered data. Undefined
   * for closed-option categories (shape, size, color…).
   */
  layerValue?: string;
};

export type CpbReadyPayload = {
  product: CpbProductState;
  layers: CpbLayer[];
};

export type CpbChangePayload = {
  product: CpbProductState;
  layers: CpbLayer[];
  /**
   * Per-category diffs with IDs resolved to labels. `prev` / `next` are null
   * when the category had no prior / new selection (rare — happens when a
   * category is dynamically added or removed).
   */
  changed: Array<{
    category: ResolvedCategory;
    prev: ResolvedOption | null;
    next: ResolvedOption | null;
  }>;
};

const readyBus = new EventTarget();
const changeBus = new EventTarget();
const logoToolBus = new EventTarget();
const LOG_STYLE = "color:#4b9;font-weight:bold;";

export type CpbLogoToolPayload = {
  action: string;
  step: number;
};

let previousLayers: CpbLayer[] = [];
let initialized = false;
let readyFired = false;

/**
 * CPB sometimes mutates existing layer objects in place between updates.
 * Keep our own immutable snapshots so prev/next diffs stay reliable.
 */
function snapshotLayers(layers: CpbLayer[]): CpbLayer[] {
  return layers.map((layer) => ({ ...layer }));
}

function log(label: string, ...args: unknown[]) {
  console.log(`%c[cpb-bridge] ${label}`, LOG_STYLE, ...args);
}

function getCpbStoreState(): CpbProductState | null {
  const cpb = (
    window as unknown as {
      CPB?: { getStore?: () => { getState: () => Record<string, unknown> } };
    }
  ).CPB;
  if (!cpb?.getStore) return null;
  try {
    const state = cpb.getStore().getState();
    return (state?.Product as CpbProductState | undefined) ?? null;
  } catch (e) {
    log("CPB.getStore().getState() threw:", e);
    return null;
  }
}

/**
 * Build lookup maps from a Product snapshot:
 *   - category id → { category, panel }
 *   - "categoryId/optionId" → option
 *
 * One pass, O(panels × categories × options). Built per-dispatch instead of
 * cached because CPB occasionally mutates the option catalogue (e.g. when a
 * sub-panel opens and loads its options async). Still much cheaper than
 * nested `.find()` chains per diff entry.
 */
type CatalogueLookup = {
  categoryById: Map<string, { cat: CpbCategory; panel: CpbPanel }>;
  optionByCatOptId: Map<string, CpbOption>;
};

let lookupCachePanelsRef: CpbPanel[] | undefined;
let lookupCacheValue: CatalogueLookup | undefined;

function buildCatalogueLookup(product: CpbProductState): CatalogueLookup {
  const panels = product.panels;
  // Memoize by `panels` reference identity. CPB rebuilds the panels array
  // only when the catalogue actually changes (sub-panel opens, options load
  // async). For typical state edits the same ref is reused across dozens of
  // CPB_ON_OPTIONS_UPDATE flushes — skipping the O(panels × cats × opts)
  // rebuild each time saves ~hundreds of allocations per second under heavy
  // input bursts.
  if (panels && panels === lookupCachePanelsRef && lookupCacheValue) {
    return lookupCacheValue;
  }

  const categoryById = new Map<string, { cat: CpbCategory; panel: CpbPanel }>();
  const optionByCatOptId = new Map<string, CpbOption>();

  for (const panel of panels ?? []) {
    for (const cat of panel.categories ?? []) {
      if (cat.id) categoryById.set(cat.id, { cat, panel });
      for (const opt of cat.options ?? []) {
        if (cat.id && opt.id) {
          optionByCatOptId.set(`${cat.id}/${opt.id}`, opt);
        }
      }
    }
  }

  lookupCachePanelsRef = panels;
  lookupCacheValue = { categoryById, optionByCatOptId };
  return lookupCacheValue;
}

function resolveCategory(
  lookup: ReturnType<typeof buildCatalogueLookup>,
  categoryId: string,
): ResolvedCategory {
  const hit = lookup.categoryById.get(categoryId);
  if (!hit) return { id: categoryId };
  return {
    id: categoryId,
    title: hit.cat.title,
    panelId: hit.panel.id,
    panelTitle: hit.panel.title,
  };
}

function resolveOption(
  lookup: ReturnType<typeof buildCatalogueLookup>,
  categoryId: string,
  optionId: string | undefined,
  layer?: CpbLayer,
): ResolvedOption | null {
  if (!optionId) return null;
  const opt = lookup.optionByCatOptId.get(`${categoryId}/${optionId}`);
  // Real data sits on `option.option.data`; `option.data` is a legacy fallback.
  const data = opt?.option?.data ?? opt?.data;
  return {
    id: optionId,
    label: data?.label,
    value: data?.value,
    layerValue: layer?.value,
  };
}

/**
 * Diff between two layer snapshots, with IDs resolved through the current
 * product catalogue.
 *
 * Single-option categories (shape, color, size preset, lighting style, …)
 * report one entry per changed category with prev/next options resolved.
 *
 * Multi-option categories (counter controls — Width + Length live under the
 * same CPB category as two distinct options) report one entry per option whose
 * selection state or free-form `layer.value` has changed. This lets downstream
 * mappers read both width and length even when only one was just edited.
 *
 * Re-emits with identical selections AND identical `layer.value` are skipped
 * so hover-only mutations inside CPB's store don't flood consumers.
 */
function diffLayers(
  prev: CpbLayer[],
  next: CpbLayer[],
  product: CpbProductState,
): CpbChangePayload["changed"] {
  const lookup = buildCatalogueLookup(product);

  const groupByCategory = (ls: CpbLayer[]) => {
    const m = new Map<string, CpbLayer[]>();
    for (const l of ls) {
      if (!l.category) continue;
      const bucket = m.get(l.category);
      if (bucket) bucket.push(l);
      else m.set(l.category, [l]);
    }
    return m;
  };

  const prevByCat = groupByCategory(prev);
  const nextByCat = groupByCategory(next);
  const out: CpbChangePayload["changed"] = [];

  const allCatIds = new Set<string>([...prevByCat.keys(), ...nextByCat.keys()]);

  for (const catId of allCatIds) {
    const prevList = prevByCat.get(catId) ?? [];
    const nextList = nextByCat.get(catId) ?? [];

    // Single-option category — classic single-select diff.
    if (prevList.length <= 1 && nextList.length <= 1) {
      const prevLayer = prevList[0];
      const nextLayer = nextList[0];
      if (
        prevLayer &&
        nextLayer &&
        prevLayer.option === nextLayer.option &&
        prevLayer.value === nextLayer.value
      ) {
        continue;
      }
      // Layer transiently detached by CPB while we still have a prev — skip.
      // mergeLayerSnapshot retains the prev; the handler can't act on a null
      // next anyway, and emitting the entry just spams the cpb-mapper logs.
      if (prevLayer && !nextLayer) continue;
      out.push({
        category: resolveCategory(lookup, catId),
        prev: resolveOption(lookup, catId, prevLayer?.option, prevLayer),
        next: resolveOption(lookup, catId, nextLayer?.option, nextLayer),
      });
      continue;
    }

    // Multi-option category (counter) — emit one entry per option whose
    // option-id or free-form value changed.
    const prevByOpt = new Map<string, CpbLayer>();
    for (const l of prevList) if (l.option) prevByOpt.set(l.option, l);
    const nextByOpt = new Map<string, CpbLayer>();
    for (const l of nextList) if (l.option) nextByOpt.set(l.option, l);
    const allOptIds = new Set<string>([...prevByOpt.keys(), ...nextByOpt.keys()]);
    for (const optId of allOptIds) {
      const p = prevByOpt.get(optId);
      const n = nextByOpt.get(optId);
      if (p && n && p.value === n.value) continue;
      out.push({
        category: resolveCategory(lookup, catId),
        prev: resolveOption(lookup, catId, p?.option, p),
        next: resolveOption(lookup, catId, n?.option, n),
      });
    }
  }
  return out;
}

function fireReady() {
  if (readyFired) return;
  const product = getCpbStoreState();
  if (!product) {
    log("ready deferred — CPB.getStore() not available yet");
    return;
  }
  readyFired = true;
  const layers = product.layers ?? [];
  previousLayers = snapshotLayers(layers);
  log("CPB ready — product state:", product);
  log("initial layers:", layers);
  readyBus.dispatchEvent(
    new CustomEvent<CpbReadyPayload>("cpb:ready", { detail: { product, layers } }),
  );
}

/**
 * CPB fires `CPB_ON_OPTIONS_UPDATE` for every keystroke / slider tick — sometimes
 * 10–20×/sec during active drag. Each emit cascaded into a viewer setState and a
 * full React-three reconciliation, which spiked frame time. We coalesce bursts
 * into one tick of work via `requestAnimationFrame`: multiple raw updates in the
 * same paint window collapse to a single diff against the snapshot we last
 * dispatched, so the consumer sees one merged change instead of N intermediate
 * ones. The diff is computed against the live `previousLayers` snapshot at flush
 * time (not at schedule time) so the merged event reflects the final settled
 * state, not stale intermediate values.
 */
let scheduledFlush = 0;
function scheduleFlush() {
  if (scheduledFlush !== 0) return;
  const raf =
    typeof requestAnimationFrame === "function"
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(performance.now()), 16) as unknown as number;
  scheduledFlush = raf(() => {
    scheduledFlush = 0;
    flushOptionsUpdate();
  });
}

/**
 * Merge a fresh layer snapshot onto the prior one, preserving any layer
 * categories that CPB transiently dropped. CPB detaches sub-controls when
 * their parent context changes (e.g. switching `lightStyle` from `text` to
 * `point` removes all `LED_RAILS_*` and `LED_TEXT_*` layers); when the user
 * comes back, CPB re-attaches them — sometimes with the user's prior choice,
 * sometimes with a default. Either way, treating the re-attach as a
 * "first-time add" (`prev: null`) re-runs the mapper handlers and silently
 * overwrites the user's selections.
 *
 * By keeping the most recent layer for every category we've ever seen in
 * `previousLayers`, the next diff compares against the user's last known
 * choice. If CPB resurrects with the same value → no diff entry, state is
 * preserved. If CPB resurrects with a different value → diff fires normally.
 */
function mergeLayerSnapshot(prev: CpbLayer[], next: CpbLayer[]): CpbLayer[] {
  const merged: CpbLayer[] = [...next];
  const presentCats = new Set<string>();
  for (const l of next) if (l.category) presentCats.add(l.category);
  for (const l of prev) {
    if (!l.category || presentCats.has(l.category)) continue;
    merged.push(l);
  }
  return merged;
}

function flushOptionsUpdate() {
  const product = getCpbStoreState();
  if (!product) return;
  const layers = product.layers ?? [];
  const layerSnapshot = snapshotLayers(layers);
  const changed = diffLayers(previousLayers, layerSnapshot, product);
  previousLayers = mergeLayerSnapshot(previousLayers, layerSnapshot);
  if (changed.length === 0) return; // layer re-emit without selection change — skip
  log("options update — changed:", changed);
  changeBus.dispatchEvent(
    new CustomEvent<CpbChangePayload>("cpb:change", {
      detail: { product, layers, changed },
    }),
  );
}

function onOptionsUpdate() {
  scheduleFlush();
}

export function initCpbBridge() {
  if (initialized) return;
  initialized = true;

  document.body.addEventListener("CPB_ON_PRODUCT_MOUNTED", fireReady);
  document.body.addEventListener("CPB_ON_OPTIONS_UPDATE", onOptionsUpdate);
  document.body.addEventListener(
    "click",
    (ev) => {
      const target = ev.target as Element | null;
      const actionEl = target?.closest?.(".cpb-upload-text .cpb-icon-button[data-action]");
      if (!actionEl) return;
      const action = actionEl.getAttribute("data-action") ?? "";
      const stepInput = document.querySelector<HTMLInputElement>(
        ".cpb-upload-text .cpb-upload-buttons-offset-input",
      );
      const stepRaw = Number.parseInt(stepInput?.value ?? "10", 10);
      const step = Number.isFinite(stepRaw) ? Math.min(200, Math.max(1, stepRaw)) : 10;
      logoToolBus.dispatchEvent(
        new CustomEvent<CpbLogoToolPayload>("cpb:logo-tool", {
          detail: { action, step },
        }),
      );
    },
    true,
  );
  document.body.addEventListener("input", (ev) => {
    const target = ev.target as HTMLInputElement | null;
    if (!target?.classList.contains("cpb-upload-buttons-offset-input")) return;
  });

  // CPB may have already mounted by the time we load — try once synchronously,
  // and if the store isn't populated yet, rely on the event listener above.
  if (getCpbStoreState()?.layers?.length) {
    log("CPB store already populated — firing ready synchronously");
    fireReady();
  } else {
    log("listening for CPB_ON_PRODUCT_MOUNTED / CPB_ON_OPTIONS_UPDATE");
  }

  (window as unknown as { __cpbBridge: unknown }).__cpbBridge = {
    readyBus,
    changeBus,
    logoToolBus,
    getProduct: () => getCpbStoreState(),
    getLayers: () => getCpbStoreState()?.layers ?? [],
  };
}

/**
 * Treat a full layer snapshot as if every layer just transitioned from "nothing"
 * to its current selection. Returns the same resolved `changed` format used by
 * `cpb:change`, so consumers can share one mapping function for both initial
 * state and incremental updates.
 */
export function resolveLayersAsChanged(
  layers: CpbLayer[],
  product: CpbProductState,
): CpbChangePayload["changed"] {
  return diffLayers([], layers, product);
}

export function onCpbReady(cb: (p: CpbReadyPayload) => void): () => void {
  // If ready already fired, replay the latest snapshot for the subscriber.
  if (readyFired) {
    const product = getCpbStoreState();
    if (product) cb({ product, layers: product.layers ?? [] });
  }
  const handler = (e: Event) => cb((e as CustomEvent<CpbReadyPayload>).detail);
  readyBus.addEventListener("cpb:ready", handler);
  return () => readyBus.removeEventListener("cpb:ready", handler);
}

export function onCpbChange(cb: (p: CpbChangePayload) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<CpbChangePayload>).detail);
  changeBus.addEventListener("cpb:change", handler);
  return () => changeBus.removeEventListener("cpb:change", handler);
}

export function onCpbLogoTool(cb: (p: CpbLogoToolPayload) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<CpbLogoToolPayload>).detail);
  logoToolBus.addEventListener("cpb:logo-tool", handler);
  return () => logoToolBus.removeEventListener("cpb:logo-tool", handler);
}
